import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

interface DeUnaResponse {
  transactionId: string;
  status: string;
  qr: string;
  numericCode: string;
}

interface Factura {
  id: number;
  emitido: string;
  vencimiento: string;
  total: number;
  estado: string;
  urlpdf: string;
}

interface DeUnaModalProps {
  show: boolean;
  onClose: () => void;
  deUnaUrl: string;
  factura: Factura;
  clienteNombre: string;
  clienteEstado: string;
}

export default function DeUnaModal({
  show,
  onClose,
  deUnaUrl,
  factura,
  clienteNombre,
  clienteEstado
}: DeUnaModalProps) {
  const [data, setData] = useState<DeUnaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(180); // 3 minutes in seconds
  const timerRef = useRef<number | null>(null);

  const handleConfirm = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        amount: factura.total,
        internalTransactionReference: factura.id.toString()
        
      };
      const resp = await axios.post<DeUnaResponse[]>(deUnaUrl, payload);
      if (resp.data.length > 0 && resp.data[0].status === '1') {
        setData(resp.data[0]);
      } else {
        setError('Error generando QR');
      }
    } catch (err: any) {
      setError(err.message || 'Error al obtener DeUna');
    } finally {
      setLoading(false);
    }
  };

  // Start countdown when data arrives
  useEffect(() => {
    if (data) {
      setCountdown(180);
      timerRef.current = window.setInterval(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
      }
    };
  }, [data]);

  // Auto-close when countdown ends
  useEffect(() => {
    if (countdown <= 0) {
      handleCloseAndReload();
    }
  }, [countdown]);

  if (!show) return null;

  const handleCloseAndReload = (): void => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
    }
    onClose();
    window.location.reload();
  };

  // Format mm:ss
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds
    .toString()
    .padStart(2, '0')}`;

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      tabIndex={-1}
      role="dialog"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          {/* Header with badge and name */}
          <div className="modal-header">
            <h5 className="modal-title">Pago DeUna</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleCloseAndReload}
              aria-label="Cerrar"
            />
          </div>

          <div className="modal-body">
            <div className="d-flex align-items-center mb-3">
              <span
                className={`badge ${
                  clienteEstado === 'ACTIVO' ? 'bg-success' : 'bg-secondary'
                }`}
                style={{ minWidth: 75 }}
              >
                {clienteEstado}
              </span>
              <strong className="ms-2">{clienteNombre}</strong>
            </div>

            {/* Countdown and QR */}
            {data && (
              <div className="text-center mb-4">
                <p>
                  <strong>Válido por:</strong> {formattedTime}
                </p>
                <img
                  src={data.qr}
                  alt="QR DeUna"
                  style={{ maxWidth: '60%', marginBottom: '1rem' }}
                />
                <p>
                  <strong>Código:</strong> {data.numericCode}
                </p>
                {/* Important message */}
                <div className="alert alert-success mt-3" role="alert">
                  <strong>Importante:</strong> Una vez realizado el pago, haz
                  clic en la <span className="fw-bold">X</span> o en{' '}
                  <span className="fw-bold">Cerrar</span> para que se efectúe
                  el pago.
                </div>
              </div>
            )}

            {/* Invoice Details */}
            <h6>Factura #{factura.id}</h6>
            <table className="table table-bordered text-center mb-3">
              <thead>
                <tr>
                  <th>Emitido</th>
                  <th>Vencimiento</th>
                  <th>Total</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{factura.emitido}</td>
                  <td>{factura.vencimiento}</td>
                  <td>
                    <strong>${factura.total.toFixed(2)}</strong>
                  </td>
                  <td>{factura.estado}</td>
                </tr>
              </tbody>
            </table>

            <p>
              <a
                href={factura.urlpdf}
                target="_blank"
                rel="noopener noreferrer"
              >
                Ver PDF de la factura
              </a>
            </p>

            {/* Generate QR Button or Loading */}
            {!data && (
              <div className="text-center mt-4">
                {error && <p className="text-danger">{error}</p>}
                {loading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-label="Cargando"
                  ></span>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={handleConfirm}
                  >
                    Generar QR
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseAndReload}
              disabled={loading}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}